import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import './TrialBanner.css';

const TrialBanner = () => {
  const { isTrialActive, daysRemaining, isTrialExpiringSoon, subscription } = useSubscription();
  const [isVisible, setIsVisible] = useState(true);
  const bannerRef = useRef(null);
  
  // Calculate if subscription is expired
  const isExpired = subscription?.subscriptionEndDate && new Date(subscription.subscriptionEndDate) < new Date();
  const isCancelled = subscription?.status === 'cancelled';
  
  // Determine validation status
  const isActive = isTrialActive || isExpired || isCancelled;

  useEffect(() => {
    console.log('TrialBanner Debug:', {
      isTrialActive,
      daysRemaining,
      subscriptionStatus: subscription?.status,
      subscriptionEndDate: subscription?.subscriptionEndDate,
      isExpired,
      isCancelled,
      isActive,
      isVisible,
      currentDate: new Date().toISOString()
    });
  }, [isTrialActive, daysRemaining, subscription, isExpired, isCancelled, isActive, isVisible]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Store dismiss state in session storage (not persistent across sessions)
    sessionStorage.setItem('trialBannerDismissed', 'true');
  };

  // Check if banner was dismissed in this session and reset visibility when subscription changes
  useEffect(() => {
    const isDismissed = sessionStorage.getItem('trialBannerDismissed');
    if (isDismissed === 'true' && isActive) {
      // If access is blocked, always show regardless of dismissal in trial mode
      if (isExpired || isCancelled) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } else if (isActive) {
      setIsVisible(true);
    }
  }, [isActive, isExpired, isCancelled]); // Re-run when subscription status changes

  // Update CSS custom property for sidebar offset on mobile
  useEffect(() => {
    const updateSidebarOffset = () => {
      if (bannerRef.current && isActive && isVisible) {
        const bannerHeight = bannerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--trial-banner-offset', `${bannerHeight}px`);
      } else {
        document.documentElement.style.setProperty('--trial-banner-offset', '0px');
      }
    };

    // Update offset when banner visibility changes
    updateSidebarOffset();

    // Update offset on window resize (in case banner height changes)
    const handleResize = () => {
      setTimeout(updateSidebarOffset, 100); // Small delay to ensure layout is complete
    };

    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (!isActive || !isVisible) {
        document.documentElement.style.setProperty('--trial-banner-offset', '0px');
      }
    };
  }, [isActive, isVisible]);

  // Don't show banner if not active/expired or not visible
  if (!isActive || !isVisible) {
    return null;
  }

  // Determine styles and messages based on state
  const isUrgent = isTrialExpiringSoon || isExpired || isCancelled;
  const isBlocked = isExpired || isCancelled;
  
  return (
    <div ref={bannerRef} className={`trial-banner ${isUrgent ? 'urgent' : ''}`}>
      <div className="trial-banner-content">
        <div className="trial-banner-text">
          <span className="trial-banner-message">
            <i className={`fas ${isUrgent ? 'fa-exclamation-triangle' : 'fa-clock'} trial-banner-inline-icon`}></i>
            {isCancelled ? (
              <>
                <strong>Tu suscripción fue cancelada.</strong> Tu información esta segura. Crea una nueva suscripción para restaurar el acceso al sistema.
              </>
            ) : isExpired ? (
              <>
                <strong>¡Su suscripción ha expirado!</strong> Por favor renueve su suscripción para continuar usando el servicio.
              </>
            ) : isTrialExpiringSoon ? (
              <>
                <strong>¡Tu período de prueba termina pronto!</strong> Solo quedan {daysRemaining} días.
              </>
            ) : (
              <>
                Período de prueba activo - <strong>{daysRemaining} días restantes</strong>
              </>
            )}
          </span>
          <span className="trial-banner-cta">
            {isCancelled ? (
              <>Elige un nuevo plan para volver a habilitar todas las funcionalidades</>
            ) : isExpired ? (
               <>Renueve ahora para restaurar el acceso completo</>
            ) : daysRemaining > 0 ? (
              <>Suscríbete ahora y mantén tus <strong>{daysRemaining} días restantes</strong> sin costo</>
            ) : (
              <>Suscríbete ahora para mantener el acceso completo</>
            )}
          </span>
        </div>
        <div className="trial-banner-actions">
          <Link to="/settings/subscription" className="trial-banner-btn primary">
            {isBlocked ? 'Ver Planes' : 'Ver Planes'}
          </Link>
          {!isBlocked && (
            <button 
              className="trial-banner-btn secondary" 
              onClick={handleDismiss}
              aria-label="Cerrar banner"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
